"use client";

import { useEffect, useMemo, useState } from "react";
import type { MarketDetail } from "@/lib/api";
import { TradePanel } from "@/components/TradePanel";
import { pickLocale } from "@/lib/format";

interface Props {
  market: MarketDetail;
  // EPIC-053 STORY-A — Polymarket-parity: the page-level
  // <OutcomeRowListShell> dispatches `granbay.multiOutcome.select` so
  // the trade panel re-binds when a row is clicked. When provided
  // statically (no row list above), defaults to highest-volume outcome.
  selectedOutcomeId?: string | null;
  defaultSide?: "yes" | "no";
  onSelectionChange?: (outcomeId: string | null) => void;
}

/**
 * Thin wrapper that selects an outcome from a multi-outcome market and
 * binds the full <TradePanel /> (the same one used for binary markets)
 * to that outcome via `boundOutcome`. The panel renders BUY YES / BUY
 * NO cards, Amount input, quick buttons, Limit Price — full parity
 * with binary surfaces. No duplicate order-form code.
 *
 * Cross-island channel: the row list lives in a separate client island
 * (left column) and the trade panel lives in the right rail; lifting
 * state to a shared parent would force the layout into one tree. The
 * window CustomEvent bridges the two without re-architecting.
 */
export function MultiOutcomeTradePanel({
  market,
  selectedOutcomeId: controlledOutcomeId,
  defaultSide,
  onSelectionChange,
}: Props) {
  // Default: highest-volume outcome (Polymarket pattern). Falls back
  // to first outcome if all volumes are zero.
  const defaultOutcomeId = useMemo(() => {
    if (market.outcomes.length === 0) return null;
    const sorted = [...market.outcomes].sort((a, b) => {
      const aVol = Number(a.volumeCents ?? 0);
      const bVol = Number(b.volumeCents ?? 0);
      return bVol - aVol;
    });
    return sorted[0]?.id ?? null;
  }, [market.outcomes]);

  const [selectedId, setSelectedId] = useState<string | null>(
    controlledOutcomeId ?? defaultOutcomeId,
  );

  // External selection (from row list) wins on render.
  useEffect(() => {
    if (controlledOutcomeId !== undefined && controlledOutcomeId !== null) {
      setSelectedId(controlledOutcomeId);
    }
  }, [controlledOutcomeId]);

  // Listen for cross-island row-click broadcast.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onSelect = (e: Event) => {
      const ce = e as CustomEvent<{ outcomeId: string; side: "yes" | "no" }>;
      if (!ce.detail?.outcomeId) return;
      setSelectedId(ce.detail.outcomeId);
      onSelectionChange?.(ce.detail.outcomeId);
    };
    window.addEventListener(
      "granbay.multiOutcome.select",
      onSelect as EventListener,
    );
    return () =>
      window.removeEventListener(
        "granbay.multiOutcome.select",
        onSelect as EventListener,
      );
  }, [onSelectionChange]);

  const selectedOutcome = useMemo(
    () => market.outcomes.find((o) => o.id === selectedId) ?? null,
    [market.outcomes, selectedId],
  );

  if (!selectedOutcome) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-[12px] text-[var(--color-text3)]">
        No outcomes available for trading.
      </div>
    );
  }

  // Multi-outcome markets: each outcome is independently a YES contract
  // priced 1-99¢ (per the matcher — see orders.service.ts:1377). The
  // "NO" side is synthetic = 100 - YES, so the user can short-sell
  // exposure to that outcome.
  const yesPrice = selectedOutcome.currentPriceCents;
  const noPrice = 100 - yesPrice;
  const label = pickLocale(selectedOutcome.name, "en");

  return (
    <TradePanel
      market={market}
      boundOutcome={{
        yesOutcomeId: selectedOutcome.id,
        yesPriceCents: yesPrice,
        // Multi-outcome NO is synthetic: same outcome row, inverse
        // price. Matcher handles it via SELL at (100 - price); the
        // user sees "BUY NO 50¢" which translates to a SELL order
        // on the outcome at 50¢. Polymarket parity.
        noOutcomeId: selectedOutcome.id,
        noPriceCents: noPrice,
        label,
        iconUrl: selectedOutcome.iconUrl,
      }}
      initialSide={defaultSide === "no" ? "no" : "yes"}
    />
  );
}
